import {Box, Flex, Title} from 'exsportia-components'
import {FC, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {AuthService} from '../../services/auth'
import {Button} from '../../ui/components/button/button'
import {OTPInput} from '../../ui/components/otp-input/otp-input'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'

type PhoneLoginOtpStepProps = {
  backAction: () => void
  phoneNumber: string
}

export const PhoneLoginOtpStep: FC<PhoneLoginOtpStepProps> = ({backAction, phoneNumber}) => {
  const {l} = useLocale()
  const dispatch = useDispatch()
  const {closeAllModals} = useModalActions()
  const isLoading = useSelector(AuthService.selectors.phoneLoginLoading())
  const error = useSelector(AuthService.selectors.phoneLoginError())

  const [otp, setOtp] = useState('')
  const [timer, setTimer] = useState(30)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000)
    }
    return () => clearInterval(interval)
  }, [timer])

  const handleVerify = () => {
    if (otp.length === 6) {
      dispatch(
        AuthService.actions.phoneLoginVerifyOtpAction({
          otp,
          callback: () => {
            closeAllModals()
            dispatch(AuthService.actions.phoneLoginResetAction())
          },
        }),
      )
    }
  }

  const handleResend = () => {
    setTimer(30)
    dispatch(AuthService.actions.phoneLoginSendOtpAction({phoneNumber}))
  }

  const displayError = error?.includes('verification code')
    ? l('invalidOtpCode', {defaultMessage: 'Invalid code, please try again'})
    : error

  return (
    <Box pb={'24px'}>
      <Flex mb='8px'>
        <Title
          text={l('checkYourMessages', {defaultMessage: 'Check your messages'})}
          styleType='h2'
        />
      </Flex>
      <Flex mb='16px'>
        <Box
          color='text-secondary'
          fontSize='14px'
        >
          {l('codeSentTo', {defaultMessage: 'Code sent to'})} {phoneNumber}
        </Box>
      </Flex>

      <Box
        my='24px'
        display='flex'
        justifyContent='center'
      >
        <OTPInput
          id='otp'
          onChange={setOtp}
          error={displayError || undefined}
        />
      </Box>

      <Box mt={'16px'}>
        <Button
          isLoading={isLoading}
          onClick={handleVerify}
          disabled={otp.length < 6 || isLoading}
        >
          {l('continue')}
        </Button>
      </Box>

      <Flex
        mt='32px'
        flexDirection='column'
        alignItems='center'
        gap='8px'
      >
        <Box
          color='text-secondary'
          fontSize='14px'
          fontWeight='medium'
        >
          {l('didntReceiveCode', {defaultMessage: 'Did not receive a code?'})}
        </Box>
        <Flex
          alignItems='center'
          fontSize='13px'
          color='text-secondary'
        >
          {timer > 0 ? (
            <Box color='#9CA3AF'>
              {l('resendCodeIn', {defaultMessage: 'Resend in '})}
              {timer}s
            </Box>
          ) : (
            <Box
              color='#ED3163'
              cursor='pointer'
              fontWeight='semibold'
              onClick={handleResend}
            >
              {l('resend', {defaultMessage: 'Resend'})}
            </Box>
          )}

          <Box mx='6px'>·</Box>

          <Box
            cursor='pointer'
            onClick={backAction}
            color='#ED3163'
            fontWeight='semibold'
          >
            {l('changeNumber', {defaultMessage: 'Change number'})}
          </Box>

          <Box mx='6px'>·</Box>

          <Box
            as='a'
            // @ts-ignore
            href='mailto:info@educationai.group'
            color='#ED3163'
            fontWeight='semibold'
            style={{textDecoration: 'none'}}
          >
            {l('contactUs', {defaultMessage: 'Contact us'})}
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}
